<template>
  <div class="block w-full min-w-0 relative group cursor-pointer" :class="size === 'small' ? 'aspect-square' : ''">
    <input
      :id="inputId"
      ref="fileRef"
      type="file"
      class="hidden"
      accept="image/*"
      @change="onFileChange"
    />
    <label
      :for="inputId"
      class="block flex rounded-3xl border-2 border-dashed transition-all w-full min-w-0 h-full min-h-[140px]"
      :class="[
        size === 'small' ? 'flex-col items-center justify-center p-4' : 'items-center gap-6 p-4 flex-wrap',
        borderClass,
        bgClass,
        isOver && 'border-[#1c4848]/30 bg-[#1c4848]/[0.02]',
        imagePreview && !isOver && 'border-[#1c4848]/20 bg-white hover:border-[#1c4848]/30 hover:shadow-sm'
      ]"
      @dragover.prevent="isOver = true"
      @dragleave.prevent="isOver = false"
      @drop.prevent="onDrop"
    >
      <template v-if="imagePreview">
        <div :class="size === 'small' ? 'w-full h-full rounded-2xl overflow-hidden relative shadow-sm bg-neutral-100' : 'w-20 h-28 rounded-xl overflow-hidden relative shrink-0 shadow-sm bg-neutral-100'">
          <img :src="imagePreview" class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" alt="" />
          <div class="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <i :class="editIconClass" class="text-white text-lg block"></i>
          </div>
        </div>
      </template>
      <template v-else>
        <div v-if="size === 'small'" class="flex flex-col items-center justify-center text-center">
          <div class="w-12 h-12 rounded-full bg-[#1c4848]/5 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
            <i class="pr pr-gallery-add text-xl text-[#1c4848]/60 block"></i>
          </div>
          <span class="text-xs font-bold text-[#1c4848] mb-1">Upload Image</span>
          <span class="text-[9px] text-[#1c4848]/40">JPG, PNG (Max 5MB)</span>
        </div>
        <div v-else class="flex-1 min-w-0">
          <h5 class="text-sm font-bold text-[#1c4848] mb-1" :class="titleClass">{{ titleText }}</h5>
          <p class="text-xs text-[#1c4848]/50 mb-3" :class="hintClass">{{ hintText }}</p>
          <div
            class="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#1c4848]/5 text-[10px] font-bold text-[#1c4848]/70"
            :class="badgeClass"
          >
            <i :class="uploadIconClass" class="block"></i>
            {{ badgeText }}
          </div>
        </div>
      </template>
    </label>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
  modelValue: { type: [String, File], default: null },
  initialPreviewUrl: { type: String, default: '' },
  size: { type: String, default: 'full' },
  label: { type: String, default: '' },
  inputId: { type: String, default: 'file-upload' },
  borderClass: { type: String, default: 'border-[#1c4848]/10' },
  bgClass: { type: String, default: 'bg-white hover:bg-[#1c4848]/[0.02]' },
  titleText: { type: String, default: 'Click to replace cover' },
  hintText: { type: String, default: 'SVG, PNG, JPG or GIF (max. 2MB)' },
  badgeText: { type: String, default: 'Upload New' },
  editIconClass: { type: String, default: 'pr pr-edit' },
  uploadIconClass: { type: String, default: 'pr pr-import-564564' },
  titleClass: { type: String, default: '' },
  hintClass: { type: String, default: '' },
  badgeClass: { type: String, default: '' }
});

const emit = defineEmits(['update:modelValue']);

const fileRef = ref(null);
const isOver = ref(false);

const imagePreview = computed(() => {
  const v = props.modelValue;
  if (typeof v === 'string' && v) return v;
  if (v instanceof File) return URL.createObjectURL(v);
  return props.initialPreviewUrl || '';
});

function setFile(file) {
  if (!file || !/^image\//.test(file.type)) return;
  emit('update:modelValue', file);
}

function onDrop(e) {
  isOver.value = false;
  const f = e.dataTransfer?.files?.[0];
  if (f) setFile(f);
}

function onFileChange(e) {
  const f = e.target?.files?.[0];
  if (f) setFile(f);
}

</script>
